import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, Printer, RefreshCw, Send, CheckCircle2, Loader2, FilePlus2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import DocumentPreviewDialog from "../shared/DocumentPreviewDialog";
import SendDocumentDialog from "./SendDocumentDialog";
import { CustomsInvoiceSummary } from "./CustomsInvoiceSummary";

export const CustomsInvoiceDialog = ({ document: initialDocument, onClose, onChanged }) => {
  const { hasPermission } = useAuth();
  const [doc, setDoc] = useState(initialDocument);
  const [notes, setNotes] = useState(initialDocument.notes || "");
  const [reference, setReference] = useState(initialDocument.customs_reference || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [mixedConfirmed, setMixedConfirmed] = useState(false);
  const isDraft = doc.lifecycle_status === "concept" && !doc.customs_finalized_at;
  const canEdit = hasPermission(doc.consolidation_id ? "consolidations:edit" : "shipments:edit");
  const canPreview = hasPermission("documents:preview");
  const dirty = notes !== (doc.notes || "") || reference !== (doc.customs_reference || "");
  const mixed = Object.keys(doc.customs_snapshot.totals.values).length > 1;
  const docId = `customs_invoice:${doc.id}`;
  const previewUrl = `/admin/document-hub/${docId}/preview`;

  const perform = async (action, payload = {}) => {
    setBusy(true); setError("");
    try {
      const { data } = await adminApi.post(`/admin/customs-invoices/${doc.id}/${action}`, payload);
      setDoc(data); setNotes(data.notes || ""); setReference(data.customs_reference || "");
      setMixedConfirmed(false); onChanged?.(data);
      return data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || formatApiError(e));
      return null;
    } finally { setBusy(false); }
  };
  const rebuild = () => perform("rebuild", { expected_revision: doc.revision || 0, notes, customs_reference: reference });
  const openPreview = async (print = false) => {
    if (dirty && !await rebuild()) return;
    setPreview({ print });
  };
  const finalize = async () => {
    const result = await perform("finalize", { expected_revision: doc.revision || 0, confirm_multiple_currencies: mixedConfirmed });
    if (result) toast.success("Douanefactuur definitief vastgelegd");
  };

  return <>
    <Dialog open={!preview && !sendOpen} onOpenChange={(open) => { if (!open && !busy && !preview && !sendOpen) onClose(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="customs-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo text-lg" data-testid="customs-dialog-title">Douanefactuur</DialogTitle>
          <DialogDescription className="break-all" data-testid="customs-document-identity">
            {doc.invoice_number} · {isDraft ? "Concept" : "Definitief snapshot"} · versie {doc.version_number || 1}
          </DialogDescription>
          <p className="text-xs text-slate-500" data-testid="customs-snapshot-date">{doc.customs_snapshot.source_label} · {new Date(doc.customs_snapshot.generated_at).toLocaleString("nl-NL")}</p>
        </DialogHeader>
        {error && <p role="alert" className="break-words bg-red-50 p-3 text-sm text-red-800" data-testid="customs-error">{error}</p>}
        <CustomsInvoiceSummary snapshot={doc.customs_snapshot} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label htmlFor="customs-reference-input">Referentie</Label>
            <Input id="customs-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} disabled={!isDraft || !canEdit || busy} maxLength={300} data-testid="customs-reference-input" /></div>
          <div><Label htmlFor="customs-notes-input">Opmerkingen</Label>
            <Textarea id="customs-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isDraft || !canEdit || busy} maxLength={4000} data-testid="customs-notes-input" /></div>
        </div>
        {mixed && isDraft && <label className="flex items-start gap-2 bg-amber-50 p-3 text-sm" data-testid="customs-mixed-currency-confirm-label">
          <input type="checkbox" checked={mixedConfirmed} onChange={(e) => setMixedConfirmed(e.target.checked)} className="mt-1" data-testid="customs-mixed-currency-confirm" />
          <span>Dit document mag meerdere valuta bevatten. De totalen blijven afzonderlijk, zonder omrekening.</span>
        </label>}
        <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
          {canPreview && <Button variant="outline" onClick={() => openPreview()} disabled={busy} data-testid="customs-preview"><Eye className="mr-1 h-4 w-4" />Preview / Download</Button>}
          {canPreview && <Button variant="outline" onClick={() => openPreview(true)} disabled={busy} data-testid="customs-print"><Printer className="mr-1 h-4 w-4" />Print</Button>}
          {isDraft && canEdit && <Button variant="outline" onClick={rebuild} disabled={busy} data-testid="customs-rebuild"><RefreshCw className="mr-1 h-4 w-4" />Opnieuw opbouwen</Button>}
          {isDraft && canEdit && <Button onClick={finalize} disabled={busy || dirty || doc.customs_needs_rebuild || doc.customs_snapshot.validation_issues.length > 0 || (mixed && !mixedConfirmed)} data-testid="customs-finalize">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}Definitief maken</Button>}
          {!isDraft && canEdit && <Button variant="outline" disabled={busy} onClick={() => perform("new-version")} data-testid="customs-new-version"><FilePlus2 className="mr-1 h-4 w-4" />Nieuw concept</Button>}
          {!isDraft && doc.lifecycle_status === "actief" && hasPermission("documents:send") && <Button disabled={busy} onClick={() => setSendOpen(true)} data-testid="customs-send"><Send className="mr-1 h-4 w-4" />{doc.last_sent_at ? "Opnieuw mailen" : "Mailen"}</Button>}
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="customs-close">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} allowDownload={hasPermission("documents:download")} title={doc.invoice_number} subtitle={isDraft ? "Concept douanefactuur" : "Definitief snapshot"}
      url={`${previewUrl}?revision=${doc.revision || 0}`} downloadUrl={`/admin/document-hub/${docId}/download`} fallbackFilename={`${doc.invoice_number}.pdf`} autoPrint={!!preview?.print} />
    {sendOpen && <SendDocumentDialog customsDocument={doc} shipment={null}
      documents={[{ key: docId, documentId: docId, title: doc.invoice_number, number: doc.invoice_number, docType: "customs_invoice", docTypeLabel: "Douanefactuur", renderable: true }]}
      preselectedKeys={[docId]} onClose={() => setSendOpen(false)} onSent={async () => { setSendOpen(false); const { data } = await adminApi.get(`/admin/customs-invoices/${doc.id}`); setDoc(data); onChanged?.(data); }} />}
  </>;
};